import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { Button, BUTTON_THEME, CheckBox } from '@st-operations/uikit/src/v2'

import User from '_lib/User'
import {
  assignDestinations,
  getMatchingDestinations,
} from '_services/salesforce-integration-partners'

import './styles.scss'

const destinationsCount = (count) => `${count} destination${count === 1 ? '' : 's'}`
const matchesCount = (count) => `${count} ${count === 1 ? 'match' : 'matches'}`

// rows with no partner first, then those with another partner, then this one's
const ORDER = { none: 0, other: 1, this: 2 }

// Finds destinations by a piece of their name, ignoring case, and assigns them
// this partner. Destinations with no partner start ticked. One that has another
// partner shows it and starts unticked, so nothing is moved off a partner unless
// you tick it. Those that have this partner already are ticked off at the bottom.
// Assigning takes the same permission as updating a destination.
const AssignDestinations = ({ partner, onAssigned }) => {
  const [search, setSearch] = useState(partner.name || '')
  const [matches, setMatches] = useState(null)
  const [selected, setSelected] = useState([])
  const [busy, setBusy] = useState(false)

  const canAssign = !!User.data?.datasphere_permissions?.destinations?.permissions?.configuration
    ?.allowed_to_update_destinations

  const partnerOf = (destination) => {
    const current = destination.salesforce_integration_partner
    if (!current?._id) return 'none'
    return current._id === partner._id ? 'this' : 'other'
  }

  const rows = [...(matches || [])].sort((a, b) => ORDER[partnerOf(a)] - ORDER[partnerOf(b)])
  const selectable = rows.filter((destination) => partnerOf(destination) !== 'this')
  const alreadyAssigned = rows.length - selectable.length
  const allSelected =
    selectable.length > 0 && selectable.every((destination) => selected.includes(destination._id))

  const find = async () => {
    const q = search.trim()
    if (!q) return

    setBusy(true)
    try {
      const { items } = await getMatchingDestinations(partner._id, q)
      setMatches(items)
      setSelected(
        items
          .filter((destination) => partnerOf(destination) === 'none')
          .map((destination) => destination._id)
      )
    } catch (e) {
      toast.error(e?.message || 'Could not find destinations')
    }
    setBusy(false)
  }

  const toggle = (id) =>
    setSelected((ids) => (ids.includes(id) ? ids.filter((other) => other !== id) : [...ids, id]))

  const toggleAll = () =>
    setSelected(allSelected ? [] : selectable.map((destination) => destination._id))

  const assign = async () => {
    setBusy(true)
    try {
      const { assigned } = await assignDestinations(partner._id, selected)
      toast.success(`Assigned ${partner.name} to ${destinationsCount(assigned)}`)
      onAssigned?.()
    } catch (e) {
      toast.error(e?.message || 'Could not assign the destinations')
    }
    setBusy(false)
    await find()
  }

  return (
    <div className="assign-destinations">
      <label className="assign-destinations__label" htmlFor="assign-destinations-search">
        Destination name contains
      </label>
      <form
        className="assign-destinations__search"
        onSubmit={(e) => {
          e.preventDefault()
          find()
        }}
      >
        <input
          id="assign-destinations-search"
          className="assign-destinations__input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button
          type="submit"
          theme={BUTTON_THEME.SECONDARY}
          className="assign-destinations__button"
          disabled={busy || !search.trim()}
        >
          Find
        </Button>
      </form>
      <p className="assign-destinations__hint">
        Matched against each destination&apos;s name and slug, ignoring case
      </p>

      <If condition={matches}>
        <div className="assign-destinations__results">
          <Choose>
            <When condition={!rows.length}>
              <p className="assign-destinations__empty">
                {`No destination matches "${search.trim()}".`}
              </p>
            </When>
            <Otherwise>
              <div className="assign-destinations__toolbar">
                <div>
                  <strong>{matchesCount(rows.length)}</strong>
                  {` · ${selected.length} selected`}
                  <If condition={alreadyAssigned}>
                    {` · ${alreadyAssigned} already ${partner.name}`}
                  </If>
                </div>
                <Choose>
                  <When condition={canAssign}>
                    <Button
                      theme={BUTTON_THEME.PRIMARY}
                      className="assign-destinations__button"
                      onClick={assign}
                      disabled={busy || !selected.length}
                    >
                      Assign to {destinationsCount(selected.length)}
                    </Button>
                  </When>
                  <Otherwise>
                    <span>Only people who can update destinations can assign partners here.</span>
                  </Otherwise>
                </Choose>
              </div>

              <div className="assign-destinations__row assign-destinations__row--head">
                <CheckBox
                  aria-label="Select all"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={!selectable.length}
                />
                <div>Destination</div>
                <div>Partner now</div>
              </div>

              <For each="destination" of={rows}>
                <div
                  key={destination._id}
                  className={classNames('assign-destinations__row', {
                    'is-assigned': partnerOf(destination) === 'this',
                  })}
                >
                  <Choose>
                    <When condition={partnerOf(destination) === 'this'}>
                      <span
                        className="assign-destinations__tick"
                        role="img"
                        aria-label={`${destination.name} already has ${partner.name}`}
                      >
                        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
                          <path
                            d="M3 8.5l3.2 3L13 4.5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          />
                        </svg>
                      </span>
                    </When>
                    <Otherwise>
                      <CheckBox
                        aria-label={`Select ${destination.name}`}
                        checked={selected.includes(destination._id)}
                        onChange={() => toggle(destination._id)}
                      />
                    </Otherwise>
                  </Choose>

                  <div className="assign-destinations__name">
                    <Link to={`/destinations/${destination._id}`}>{destination.name}</Link>
                    <span className="assign-destinations__slug">{destination.slug}</span>
                  </div>

                  <Choose>
                    <When condition={partnerOf(destination) === 'other'}>
                      <div>{destination.salesforce_integration_partner.name}</div>
                    </When>
                    <When condition={partnerOf(destination) === 'this'}>
                      <div className="assign-destinations__quiet">{partner.name}</div>
                    </When>
                    <Otherwise>
                      <div className="assign-destinations__quiet">None</div>
                    </Otherwise>
                  </Choose>
                </div>
              </For>
            </Otherwise>
          </Choose>
        </div>
      </If>
    </div>
  )
}

export default AssignDestinations
